import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import * as api from '../../api/educentialApi';

const ContentEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!id;

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    content: '',
    contentType: 'guide',
    category: 'general',
    fileUrl: '',
    published: false,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fetchingContent, setFetchingContent] = useState(isEditMode);

  useEffect(() => {
    if (isEditMode) {
      fetchContent();
    }
  }, [id]);

  const fetchContent = async () => {
    try {
      setFetchingContent(true);
      const response = await api.getContentById(parseInt(id));
      setFormData({
        title: response.data.title,
        description: response.data.description || '',
        content: response.data.content,
        contentType: response.data.contentType,
        category: response.data.category,
        fileUrl: response.data.fileUrl || '',
        published: response.data.published,
      });
    } catch (err) {
      console.error('Error fetching content:', err);
      setError('Failed to load content');
    } finally {
      setFetchingContent(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (isEditMode) {
        await api.updateContent(parseInt(id), formData);
      } else {
        await api.createContent(formData);
      }
      navigate('/educator/content');
    } catch (err) {
      console.error('Error saving content:', err);
      setError(err.message || 'Failed to save content');
    } finally {
      setLoading(false);
    }
  };

  if (fetchingContent) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading content...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/educator/content"
            className="text-teal-600 hover:text-teal-700 mb-4 inline-block"
          >
            ← Back to Content Library
          </Link>
          <h1 className="text-3xl font-bold text-gray-900">
            {isEditMode ? 'Edit Content' : 'Create New Content'}
          </h1>
          <p className="text-gray-600 mt-1">
            {isEditMode ? 'Update your educational content' : 'Share your knowledge with students'}
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-6 space-y-6">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
              placeholder="Enter content title..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={3}
              placeholder="Brief description of the content..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
          </div>

          {/* Content Type and Category */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Content Type <span className="text-red-500">*</span>
              </label>
              <select
                name="contentType"
                value={formData.contentType}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              >
                <option value="course">Course</option>
                <option value="resource">Resource</option>
                <option value="guide">Guide</option>
                <option value="video">Video</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Category <span className="text-red-500">*</span>
              </label>
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              >
                <option value="general">General</option>
                <option value="test-prep">Test Preparation</option>
                <option value="overseas">Overseas Education</option>
                <option value="skills">Skill Development</option>
                <option value="career">Career Development</option>
              </select>
            </div>
          </div>

          {/* Content */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Content <span className="text-red-500">*</span>
            </label>
            <textarea
              name="content"
              value={formData.content}
              onChange={handleChange}
              required
              rows={12}
              placeholder="Enter your content here (supports markdown)..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent font-mono text-sm"
            />
            <p className="mt-1 text-xs text-gray-500">
              Tip: You can use markdown formatting for better readability
            </p>
          </div>

          {/* File URL (Optional) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              File URL (Optional)
            </label>
            <input
              type="url"
              name="fileUrl"
              value={formData.fileUrl}
              onChange={handleChange}
              placeholder="https://example.com/file.pdf"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
            <p className="mt-1 text-xs text-gray-500">
              Optional: Link to external files, documents, or resources
            </p>
          </div>

          {/* Publish Checkbox */}
          <div className="flex items-center">
            <input
              type="checkbox"
              name="published"
              checked={formData.published}
              onChange={handleChange}
              className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
            />
            <label className="ml-2 block text-sm text-gray-700">
              Publish this content immediately
            </label>
          </div>

          {/* Actions */}
          <div className="flex gap-4 pt-4">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 font-medium disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {loading ? 'Saving...' : isEditMode ? 'Update Content' : 'Create Content'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/educator/content')}
              className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium"
            >
              Cancel
            </button>
          </div>
        </form>

        {/* Preview Section */}
        {formData.content && (
          <div className="mt-8 bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Content Preview</h2>
            <div className="prose max-w-none">
              <h3 className="text-xl font-bold mb-2">{formData.title || 'Untitled'}</h3>
              {formData.description && (
                <p className="text-gray-600 mb-4">{formData.description}</p>
              )}
              <div className="whitespace-pre-wrap text-gray-800">{formData.content}</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContentEditor;
